import { HTTPError } from "@gocardless/api/utils/api";
import { useState } from "react";
import _ from "lodash";

import { SignUpAccountType } from "../SignUp";

import { SignupFormData } from "./consts";
import { useEmailVerification } from "./useEmailVerification";
import { useRegistration } from "./useRegistration";
import { getReferralEmailVariation, getReferralSource } from "./helper";

import { useRouterQuery } from "src/common/routing";
import { CountryCodeSupported } from "src/common/country";

interface UseSignupProps {
  onEmailVerificationSuccess: () => void;
  onEmailVerificationError: (error: HTTPError) => void;
  userCountryCode: string;
  accountType: SignUpAccountType;
  setShowSignupForm: (arg: boolean) => void;
}

interface UseSignup {
  submitEmailVerificationRegistration: (verificationCode: string) => void;
  createEmailVerification: (formData: SignupFormData) => void;
  signupFormData?: SignupFormData;
  isSubmittingEmailVerification: boolean;
  isSubmittingRegistration: boolean;
  setShowSignupForm: (arg: boolean) => void;
}

export const useSignup = ({
  onEmailVerificationSuccess,
  onEmailVerificationError,
  userCountryCode,
  accountType,
  setShowSignupForm,
}: UseSignupProps): UseSignup => {
  const { item: referralGeo } = useRouterQuery("referral_geo");
  const { item: referralCode } = useRouterQuery("referral_code");
  const { item: referralCodeName } = useRouterQuery("referral_code_name");
  const { item: fsbMemberDiscount } = useRouterQuery("fsb_member_discount");
  const { item: defaultReferralCodeName } = useRouterQuery("name");
  const { item: defaultReferralCode } = useRouterQuery("code");
  const { item: defaultReferralSource } = useRouterQuery("s");
  const { item: defaultReferralEmailVariation } = useRouterQuery("v");

  const referralSource = getReferralSource(
    defaultReferralSource,
    defaultReferralEmailVariation
  );
  const referralEmailVariation = getReferralEmailVariation(
    defaultReferralEmailVariation
  );

  const referralCodeNameValue = defaultReferralCodeName
    ? _.capitalize(defaultReferralCodeName)
    : referralCodeName;

  const hasFsbMemberDiscount = !!fsbMemberDiscount;

  const [signupFormData, setSignupFormData] = useState<SignupFormData>();

  const defaultCountryCode =
    userCountryCode in CountryCodeSupported
      ? (userCountryCode as CountryCodeSupported)
      : CountryCodeSupported.GB;
  const {
    createEmailVerification,
    isSubmitting: isSubmittingEmailVerification,
  } = useEmailVerification({
    onSuccess: onEmailVerificationSuccess,
    onError: onEmailVerificationError,
  });

  const {
    submitEmailVerificationRegistration,
    isSubmitting: isSubmittingRegistration,
  } = useRegistration({
    defaultCountryCode,
    registrationParams: {
      ...signupFormData,
      referralGeo,
      referralCode: defaultReferralCode ?? referralCode,
      accountType,
      hasFsbMemberDiscount,
      referralSource,
      referralEmailVariation,
      referralCodeName: referralCodeNameValue,
    },
    setShowSignupForm,
  });

  const initiateEmailVerification = (formData: SignupFormData) => {
    setSignupFormData(formData);
    createEmailVerification(formData);
  };

  return {
    submitEmailVerificationRegistration,
    createEmailVerification: initiateEmailVerification,
    signupFormData,
    isSubmittingEmailVerification,
    isSubmittingRegistration,
    setShowSignupForm,
  };
};
